import {
  ConfirmWayPurchaseRequest,
  WayPurchaseNotificationData,
  WayPurchaseSingleWashDisplayVm,
  WayNormalizedPurchase,
} from '../models/way-purchase.models';
import { formatWayPurchaseChannel } from './way-purchase-channel.util';
import {
  pickString,
  readPurchase,
  readRecord,
  resolveLicensePlate,
} from './way-purchase-display.helpers';

export function buildWayPurchaseSingleWashDisplayVm(
  data: WayPurchaseNotificationData | Record<string, any> | null | undefined
): WayPurchaseSingleWashDisplayVm | null {
  const row = readRecord(data);
  const purchase = readPurchase(row);
  if (!purchase) {
    return null;
  }

  const purchaseType = String(purchase.purchase_type ?? '').trim().toLowerCase();
  if (purchaseType && purchaseType !== 'single_wash') {
    return null;
  }

  return {
    customerName: pickString(purchase.customer_name) || '—',
    packageName: pickString(purchase.package_name) || '—',
    licensePlate: resolveLicensePlate(row, purchase),
    channelLabel: formatWayPurchaseChannel(purchase.purchase_channel),
  };
}

function resolveConfirmPurchaseType(
  purchase: WayNormalizedPurchase | null | undefined
): 'membership' | 'single_wash' | null {
  const normalized = String(purchase?.purchase_type ?? '').trim().toLowerCase();
  if (normalized === 'membership') {
    return 'membership';
  }
  if (normalized === 'single_wash' || normalized === 'single-wash') {
    return 'single_wash';
  }
  return null;
}

function resolveConfirmPricePaid(value: any): number | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function buildConfirmWayPurchaseRequest(
  data: WayPurchaseNotificationData | Record<string, any> | null | undefined,
  purchaseOverride?: WayNormalizedPurchase | null
): ConfirmWayPurchaseRequest | null {
  const row = readRecord(data);
  const purchase = purchaseOverride ?? readPurchase(row);
  const wayPassId = pickString(purchase?.way_pass_id);
  const lprEntryId = pickString(row?.['lpr_entry_id'], row?.['lprEntryId'], row?.['id']);
  const locationId = pickString(row?.['location_id'], row?.['locationId']);
  const localPackageId = pickString(row?.['local_package_id'], purchase?.local_package_id);
  const purchaseType = resolveConfirmPurchaseType(purchase);
  // Prefer Way plate (or attendant-merged purchase.plate_number); never LPR CA-prefixed plate.
  const plateNumber = pickString(purchase?.plate_number);
  const roiId = pickString(row?.['roi_id'], row?.['roiID'], row?.['roiId']);

  if (!wayPassId || !locationId || !purchaseType) {
    return null;
  }

  const pricePaid = resolveConfirmPricePaid(purchase?.price_paid);
  const packageName = pickString(purchase?.package_name);
  const purchaseChannel = pickString(purchase?.purchase_channel);
  const wayPackageId = pickString(purchase?.package_id);
  const customerId = pickString(row?.['customer_id'], row?.['customerId']);
  const vehicleId = pickString(row?.['vehicle_id'], row?.['vehicleId']);
  const customerName = pickString(purchase?.customer_name, row?.['customer_name'], row?.['customerName']);
  const customerEmail = pickString(purchase?.customer_email, row?.['customer_email'], row?.['customerEmail']);
  const customerPhone = pickString(
    purchase?.customer_phone,
    row?.['customer_phone'],
    row?.['customerPhone'],
    row?.['customer_contact'],
    row?.['customerContact']
  );
  const washesRemaining =
    purchaseType === 'membership' ? (purchase?.washes_remaining ?? null) : undefined;

  return {
    way_pass_id: wayPassId,
    location_id: locationId,
    purchase_type: purchaseType,
    ...(lprEntryId ? { lpr_entry_id: lprEntryId } : {}),
    ...(localPackageId ? { local_package_id: localPackageId } : {}),
    ...(wayPackageId ? { way_package_id: wayPackageId } : {}),
    ...(packageName ? { package_name: packageName } : {}),
    ...(pricePaid !== undefined ? { price_paid: pricePaid } : {}),
    ...(purchaseChannel ? { purchase_channel: purchaseChannel } : {}),
    ...(plateNumber ? { plate_number: plateNumber } : {}),
    ...(customerId ? { customer_id: customerId } : {}),
    ...(vehicleId ? { vehicle_id: vehicleId } : {}),
    ...(customerName ? { customer_name: customerName } : {}),
    ...(customerEmail ? { customer_email: customerEmail } : {}),
    ...(customerPhone ? { customer_phone: customerPhone } : {}),
    ...(roiId ? { roi_id: roiId } : {}),
    ...(purchaseType === 'membership' ? { washes_remaining: washesRemaining } : {}),
  };
}
